import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  StyleSheet,
  Image,
} from 'react-native';
import { MoneyInput } from './MoneyInput';
import { useTheme } from '@/contexts/ThemeContext';
import { ModoDivisao, DivisaoPersonalizada } from '@/types/app';
import { CheckCircle, AlertCircle } from 'lucide-react-native';
import { logger } from '../utils/logger';

interface Participante {
  id: string;
  nome: string;
  foto: string;
}

interface DivisaoDespesaProps {
  participantes: Participante[];
  valorTotal: number;
  modoDivisao: ModoDivisao;
  divisoesPersonalizadas: DivisaoPersonalizada[];
  onModoDivisaoChange: (modo: ModoDivisao) => void;
  onDivisoesChange: (divisoes: DivisaoPersonalizada[]) => void;
}

export default function DivisaoDespesa({
  participantes,
  valorTotal,
  modoDivisao,
  divisoesPersonalizadas,
  onModoDivisaoChange,
  onDivisoesChange,
}: DivisaoDespesaProps) {
  const { colors } = useTheme();
  const valorPorPessoa = React.useMemo(() => {
    if (modoDivisao === 'igual' && participantes.length > 0) {
      const valorTotalNumerico = typeof valorTotal === 'number' ? valorTotal : 0;
      return valorTotalNumerico > 0 ? valorTotalNumerico / participantes.length : 0;
    }
    return 0;
  }, [modoDivisao, valorTotal, participantes]);

  // Atualiza divisões igual apenas se necessário
  React.useEffect(() => {
    if (modoDivisao === 'igual' && participantes.length > 0) {
      // Criar mapa para comparação eficiente
      const divisoesAtuaisMap = new Map(divisoesPersonalizadas.map(d => [d.usuarioId, d.valorDevido]));
      
      let precisaAtualizar = false;
      
      // Verificar se número de participantes mudou
      if (divisoesPersonalizadas.length !== participantes.length) {
        precisaAtualizar = true;
      } else {
        // Verificar se algum valor ou participante mudou
        for (const p of participantes) {
          const valorAtual = divisoesAtuaisMap.get(p.id);
          // Usar pequena tolerância para float
          if (valorAtual === undefined || Math.abs(valorAtual - valorPorPessoa) > 0.001) {
            precisaAtualizar = true;
            break;
          }
        }
      }

      if (precisaAtualizar) {
        const divisoesAtualizadas = participantes.map(p => ({
          usuarioId: p.id,
          nome: p.nome,
          valorDevido: valorPorPessoa,
        }));
        onDivisoesChange(divisoesAtualizadas);
      }
    }
  }, [modoDivisao, valorPorPessoa, participantes, divisoesPersonalizadas, onDivisoesChange]);

  // Inicializar divisões personalizadas apenas se necessário
  React.useEffect(() => {
    if (modoDivisao === 'personalizado' && (!divisoesPersonalizadas || divisoesPersonalizadas.length === 0)) {
      const novasDivisoes = participantes.map(p => ({
        usuarioId: p.id,
        nome: p.nome,
        valorDevido: 0,
      }));
      onDivisoesChange(novasDivisoes);
    }
  }, [modoDivisao, participantes, divisoesPersonalizadas, onDivisoesChange]);

  const handleValorChange = (usuarioId: string, valor: string) => {
    // Aceita apenas números e vírgula, substituindo vírgula por ponto para cálculos
    try {
      const valorLimpo = (valor || '').replace(/[^\d,]/g, '').replace(/,/g, '.');
      
      // Se o valor estiver vazio ou inválido, define como 0
      const novoValor = valorLimpo === '' ? 0 : parseFloat(valorLimpo);
      
      // Atualiza apenas se for um número válido
      if (!isNaN(novoValor)) {
        if (!Array.isArray(divisoesPersonalizadas)) {
          onDivisoesChange([{
            usuarioId,
            nome: participantes.find(p => p.id === usuarioId)?.nome || '',
            valorDevido: novoValor
          }]);
          return;
        }
        
        const novasDivisoes = divisoesPersonalizadas.map(divisao => 
          divisao.usuarioId === usuarioId 
            ? { ...divisao, valorDevido: novoValor }
            : divisao
        );
        onDivisoesChange(novasDivisoes);
      }
    } catch (error) {
      logger.error('Error processing value', { error, usuarioId, valor });
      // Em caso de erro, mantém o valor como 0
      const novasDivisoes = (divisoesPersonalizadas || []).map(divisao => 
        divisao.usuarioId === usuarioId 
          ? { ...divisao, valorDevido: 0 }
          : divisao
      );
      onDivisoesChange(novasDivisoes);
    }
  };

  const calcularTotal = () => {
    if (!Array.isArray(divisoesPersonalizadas)) return 0;
    return divisoesPersonalizadas.reduce((total, divisao) => {
      const valorDevido = typeof divisao.valorDevido === 'number' ? divisao.valorDevido : 0;
      return total + valorDevido;
    }, 0);
  };

  const totalCalculado = calcularTotal();
  const valorTotalNumerico = typeof valorTotal === 'number' ? valorTotal : 0;
  const diferenca = valorTotalNumerico - totalCalculado;
  const isValido = Math.abs(diferenca) < 0.01; // Tolerância de 1 centavo

  const formatarValor = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(valor);
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={[styles.title, { color: colors.text }]}>Como dividir a despesa?</Text>
        
        <View style={styles.modoSelector}>
          <TouchableOpacity
            style={[
              styles.modoButton,
              modoDivisao === 'igual' && { backgroundColor: colors.primary + '20' }
            ]}
            onPress={() => onModoDivisaoChange('igual')}
          >
            <Text style={[
              styles.modoButtonText,
              { color: modoDivisao === 'igual' ? colors.primary : colors.textSecondary }
            ]}>
              Igual
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[
              styles.modoButton,
              modoDivisao === 'personalizado' && { backgroundColor: colors.primary + '20' }
            ]}
            onPress={() => onModoDivisaoChange('personalizado')}
          >
            <Text style={[
              styles.modoButtonText,
              { color: modoDivisao === 'personalizado' ? colors.primary : colors.textSecondary }
            ]}>
              Personalizado
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {modoDivisao === 'igual' ? (
      <View style={styles.divisaoIgual}>
        <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
          Valor por pessoa: {formatarValor(valorPorPessoa)}
        </Text>
        <View style={styles.participantesList}>
          {participantes.map((participante) => (
            <View key={participante.id} style={styles.participanteItem}>
              <Image source={{ uri: participante.foto }} style={styles.participanteFoto} />
              <Text style={[styles.participanteNome, { color: colors.text }]}>
                {participante.nome}
              </Text>
              <Text style={[styles.participanteValor, { color: colors.primary }]}>
                {formatarValor(valorPorPessoa)}
              </Text>
            </View>
          ))}
        </View>
      </View>
      ) : (
        <View style={styles.divisaoPersonalizada}>
          <View style={styles.totalInfo}>
            <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
              Total da despesa: {formatarValor(valorTotal)}
            </Text>
            <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
              Total definido: {formatarValor(totalCalculado)}
            </Text>
            
            {!isValido && (
              <View style={styles.erroContainer}>
                <AlertCircle size={16} color={colors.error} />
                <Text style={[styles.erroText, { color: colors.error }]}>
                  Diferença: {formatarValor(diferenca)}
                </Text>
              </View>
            )}
            
            {isValido && (
              <View style={styles.sucessoContainer}>
                <CheckCircle size={16} color={colors.success} />
                <Text style={[styles.sucessoText, { color: colors.success }]}>
                  Valores corretos!
                </Text>
              </View>
            )}
          </View>

          <View style={styles.participantesList}>
            {divisoesPersonalizadas.map((divisao) => {
              const participante = participantes.find(p => p.id === divisao.usuarioId);
              if (!participante) return null;

              return (
                <View key={divisao.usuarioId} style={styles.participanteItem}>
                  <Image source={{ uri: participante.foto }} style={styles.participanteFoto} />
                  <Text style={[styles.participanteNome, { color: colors.text }]}>
                    {participante.nome}
                  </Text>
                  <MoneyInput
                    style={[
                      styles.valorInput,
                      { 
                        color: colors.text,
                        backgroundColor: colors.surface,
                        borderColor: colors.border,
                        paddingHorizontal: 8 // Reduzir padding para caber
                      }
                    ]}
                    textAlign="right"
                    prefix="R$"
                    placeholder="0,00"
                    value={divisao.valorDevido > 0 ? divisao.valorDevido.toFixed(2).replace('.', ',') : ''}
                    onChangeValue={(text) => handleValorChange(divisao.usuarioId, text)}
                  />
                </View>
              );
            })}
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 24,
  },
  header: {
    marginBottom: 16,
  },
  title: {
    fontSize: 16,
    fontFamily: 'Inter-SemiBold',
    marginBottom: 12,
  },
  modoSelector: {
    flexDirection: 'row',
    gap: 8,
  },
  modoButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'transparent',
  },
  modoButtonText: {
    fontSize: 14,
    fontFamily: 'Inter-Medium',
  },
  divisaoIgual: {
    gap: 12,
  },
  divisaoPersonalizada: {
    gap: 16,
  },
  subtitle: {
    fontSize: 14,
    fontFamily: 'Inter-Regular',
    marginBottom: 4,
  },
  totalInfo: {
    gap: 4,
  },
  erroContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
  },
  erroText: {
    fontSize: 14,
    fontFamily: 'Inter-Medium',
  },
  sucessoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
  },
  sucessoText: {
    fontSize: 14,
    fontFamily: 'Inter-Medium',
  },
  participantesList: {
    gap: 12,
  },
  participanteItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 8,
  },
  participanteFoto: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  participanteNome: {
    flex: 1,
    fontSize: 14,
    fontFamily: 'Inter-Medium',
  },
  participanteValor: {
    fontSize: 14,
    fontFamily: 'Inter-SemiBold',
  },
  valorInput: {
    width: 80,
    height: 40,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
    fontFamily: 'Inter-Regular',
    textAlign: 'center',
    borderWidth: 1,
  },
});